import Head from "next/head";
import type { FaqItem } from "./LandingFaq";

export const SITE_URL = 'https://dappergpt.com';
export const SITE_NAME = 'DapperGPT';
const DEFAULT_IMAGE = '/page/dappergpt-preview.png';

interface SeoHeadProps {
    title: string;
    description: string;
    /**
     * Canonical path for this content. Alias URLs that render the same page
     * must all point here, so search engines index one URL instead of treating
     * dozens of identical pages as duplicates.
     */
    path: string;
    image?: string;
    /** Emits FAQPage structured data; pass the same items the page renders. */
    faq?: FaqItem[];
    /** Emits SoftwareApplication structured data for product pages. */
    product?: boolean;
    noindex?: boolean;
    type?: 'website' | 'article';
}

/** Escape `<` so JSON-LD can't close its own <script> tag. */
const jsonLd = (data: unknown) => ({ __html: JSON.stringify(data).replace(/</g, '\\u003c') });

export const SeoHead = ({ title, description, path, image = DEFAULT_IMAGE, faq, product, noindex, type = 'website' }: SeoHeadProps) => {
    const url = `${SITE_URL}${path === '/' ? '' : path}`;
    const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex,follow" />}

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={imageUrl} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />

            {product && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={jsonLd({
                        '@context': 'https://schema.org',
                        '@type': 'SoftwareApplication',
                        name: SITE_NAME,
                        url: SITE_URL,
                        applicationCategory: 'ProductivityApplication',
                        operatingSystem: 'Web, Chrome',
                        description,
                        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
                    })}
                />
            )}
            {faq && faq.length > 0 && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={jsonLd({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: faq.map(item => ({
                            '@type': 'Question',
                            name: item.question,
                            acceptedAnswer: { '@type': 'Answer', text: item.answer },
                        })),
                    })}
                />
            )}
        </Head>
    );
};
